import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  FormHelperText,
  Grid,
  OutlinedInput,
  Stack,
  Typography,
  Button,
  Avatar
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { components } from 'react-select';
import { apiRequest } from 'helpers/apiRequests';
import ConfirmationDialog from 'components/@extended/ConfirmationDialog';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import TimezoneSelector from 'components/@extended/TimezoneSelector';

const CustomTypography = styled(Typography)(({ theme, width }) => ({
  position: 'relative',
  '&:after': {
    content: "''",
    width: `calc(100% - ${width}px)`,
    position: 'absolute',
    right: '0',
    bottom: '8px',
    borderBottom: '1px dashed #D9D9D9'
  }
}));

const CustomOption = (props) => {
  return (
    <>
      <components.Option {...props}>
        <Stack
          direction={'row'}
          spacing={1}
        >
          <Box>
            <Avatar
              sx={{ width: '40px', height: '40px', border: `2px solid ${props?.data.disc_style_color}` }}
              src={props?.data.profile_image}
              alt={props?.data.manager}
              size={'small'}
            />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={500}
              fontSize={'15px'}
            >
              {props.data?.manager}
            </Typography>
            <Typography
              variant="subtitle1"
              fontSize={'14px'}
            >
              {props.data?.email}
            </Typography>
          </Box>
        </Stack>
      </components.Option>
    </>
  );
};

const USER_ROLES = [
  {
    tenant_specific_role: 'Client Super Admin'
  },
  {
    tenant_specific_role: 'Client Admin'
  },
  {
    tenant_specific_role: 'Observer'
  },
  {
    tenant_specific_role: 'User'
  }
];
const autocompleteStyle = {
  '.MuiOutlinedInput-root': {
    padding: '3px'
  },
  '.MuiAutocomplete-popupIndicator': {
    height: '30px'
  },
  '.MuiAutocomplete-clearIndicator': {
    height: '30px'
  }
};

const Form = ({ formDetails, companyDetail, profileImage, userData }) => {
  const { userDetails } = useSelector((state) => state.user);
  const [isOpenModal, setOpenModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpenHeadModal, setIsOpenHeadModal] = useState(false);
  const [isOpenHODModal, setIsOpenHODModal] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();


  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(
      Yup.object({
        tenant_id: Yup.string().required('Company is required.'),
        tenant: Yup.string().required('Company is required.'),
        firstName: Yup.string().required('This field may not be blank.'),
        lastName: Yup.string().required('This field may not be blank.'),
        email: Yup.string().email('Invalid email.').required('This field may not be blank.'),
        child_tenants: Yup.array()
          .of(
            Yup.object({
              tenant_id: Yup.number().required(),
              role: Yup.string().required('Role is required')
            })
          )
          .length(formDetails?.group_companies?.length, `All ${formDetails?.group_companies?.length} companies must have a role assigned`)
        // role: Yup.string().required('Please select user role.').nullable()
      })
    ),
    defaultValues: {
      tenant_id: companyDetail?.company?.company_id ?? companyDetail?.company?.id,
      tenant: companyDetail?.company?.company_name,
      firstName: '',
      lastName: '',
      email: '',
      title: '',
      discStyle: '',
      // role: '',
      // manager: 31,
      // branch: '',
      // department: [],
      timezone: userDetails?.logged_in_user_timezone,
      child_tenants: []
      // radioGroup: [],
      // orgGroup: []
    }
  });

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const profile = profileImage?.split('/');
      const reqObj = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        tenant: formData.tenant_id,
        disc_style: formData.discStyle,
        job_title: formData?.title,
        profile_image: profile?.[profile?.length - 1],
        time_zone: formData.timezone?.value ?? formData.timezone,
      };
      if (params.userId) {
        reqObj['id'] = params.userId;
      } else {
        reqObj['child_tenants'] = formData?.child_tenants;
      }
      const response = params?.userId
        ? await apiRequest.editGroupCompanyUser(params?.userId, reqObj)
        : await apiRequest.addGroupCompanyUser(reqObj);
      // const response = await apiRequest.addCompanyUser(reqObj);
      if (response?.data?.status === 200) {
        toast.success(response?.data?.message);
        navigate(-1);
      }
    } catch (error) {
      if (error?.response?.data?.meta_data?.error_fields) {
        // toast.error(showErrors(error?.response?.data?.meta_data?.error_fields), {
        //   autoClose: 6000
        // });
      } else {
        toast.error(error?.response?.data?.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (userData) {
      setValue('firstName', userData?.first_name);
      setValue('lastName', userData?.last_name);
      setValue('email', userData?.email);
      setValue('title', userData?.job_title);
      let discStyleVal = userData?.disc_style;
      if (discStyleVal && typeof discStyleVal === 'string') {
        if (!isNaN(discStyleVal)) {
          discStyleVal = parseInt(discStyleVal);
        } else {
          const match = formDetails?.company_disc_details?.find((item) => item.disc_style === discStyleVal);
          if (match) {
            discStyleVal = match.id;
          }
        }
      }
      setValue('discStyle', discStyleVal || '');
      // setValue('role', userData?.role);
      // setValue('branch', userData?.branch);
      // setValue(
      //   'department',
      //   formDetails?.company_department_details?.filter((item) =>
      //     userData?.department?.map((dpt) => dpt?.department_id)?.includes(item?.id)
      //   )
      // );
      // setValue(
      //   'headOfDepartment',
      //   formDetails?.company_department_details?.filter((item) => item?.department_head?.id === userData?.id)?.map((item) => item?.id)
      // );
      setValue('timezone', userData?.time_zone ?? userDetails?.logged_in_user_timezone);
      // setValue('manager', formDetails?.company_manager_details?.filter((item) => item?.id === parseInt(userData?.manager))?.[0] ?? '');
      // setValue('radioGroup', [userData?.is_ceo ? 'ceo' : '', userData?.is_manager ? 'manager' : '']);
      // setValue('orgGroup', [userData?.org_chart_hidden === true ? 'hide' : '']);
      // setUserRole(userData?.role);
      if (formDetails?.group_companies) {
        const childTenantsMapped = formDetails.group_companies.map((company) => {
          const userCompany = (userData?.child_company_users || userData?.child_tenants)?.find(
            (item) => item.tenant_id === company.id
          );
          return {
            tenant_id: company.id,
            role: userCompany ? userCompany.role : ''
          };
        });
        setValue('child_tenants', childTenantsMapped);
      } else {
        setValue('child_tenants', userData?.child_tenants || []);
      }
    } else {
      setValue('timezone', userDetails?.logged_in_user_timezone);
    }
    // eslint-disable-next-line
  }, [userData, formDetails]);

  const handleClose = (status) => {
    if (status) {
      const radioGroupValue = getValues('radioGroup');
      if (status) {
        setValue('radioGroup', [...radioGroupValue, 'ceo']);
      }
      setOpenModal(false);
    } else {
      setOpenModal(false);
    }
  };

  const childTenants = watch('child_tenants');

  const handleCloseHeadModal = (status) => {
    const headGroupValue = getValues('headOfDepartment');
    if (status) {
      setValue('headOfDepartment', [...headGroupValue, isOpenHeadModal]);
      setIsOpenHeadModal(false);
    } else {
      setIsOpenHeadModal(false);
    }
  };

  const handleCloseHODModal = (status) => {
    if (status) {
      const hodIds = getValues('headOfDepartment');
      const department = getValues('department');
      const filterHOD = hodIds?.filter((id) => id !== isOpenHODModal?.id);
      setValue('headOfDepartment', filterHOD);
      setValue(
        'department',
        department?.filter((dpt) => dpt?.id !== isOpenHODModal?.id)
      );
      setIsOpenHODModal(false);
    } else {
      setIsOpenHODModal(false);
    }
  };

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box>
        <Box sx={{ my: '15px' }}>
          <CustomTypography
            sx={{ fontWeight: 500, opacity: 0.5 }}
            variant="body2"
            color={theme.palette.slate[900]}
            width={'155'}
          >
            User Information
          </CustomTypography>
        </Box>
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={6}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="tenant">Company</InputLabel>
              <Controller
                name="tenant"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <OutlinedInput
                    readOnly
                    disabled={Boolean(params?.userId)}
                    id="tenant"
                    type="text"
                    value={field.value}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    placeholder=""
                    fullWidth
                    error={Boolean(errors.tenant)}
                  />
                )}
              />
              {errors.tenant?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-tenant"
                >
                  {errors.tenant?.message}
                </FormHelperText>
              )}
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="discStyle">DiSC Style</InputLabel>
              <Controller
                name="discStyle"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <MuiSelect
                    displayEmpty
                    id="discStyle"
                    autoComplete={'on'}
                    value={field?.value ?? ''}
                    name={field.name}
                    onBlur={field.onBlur}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 250 // Set the desired max height for the dropdown
                        }
                      }
                    }}
                    onChange={field.onChange}
                    fullWidth
                    error={Boolean(errors.discStyle)}
                    // renderValue={(selected) => {
                    //   if (selected?.length === 0) {
                    //     return <em>State / Province</em>;
                    //   }
                    //   return selected;
                    // }}
                  >
                    <MenuItem
                      disabled
                      value=""
                    >
                      Select DiSC Style
                    </MenuItem>
                    {formDetails?.company_disc_details?.map((item) => (
                      <MenuItem
                        key={item?.id}
                        value={item.id}
                      >
                        {item.disc_style}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                )}
              />
              {errors.discStyle?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-discStyle"
                >
                  {errors.discStyle?.message}
                </FormHelperText>
              )}
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="firstName">
                First Name<span style={{ color: 'red' }}> *</span>
              </InputLabel>
              <Controller
                name="firstName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <OutlinedInput
                    id="firstName"
                    type="text"
                    autoComplete={'on'}
                    value={field.value}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    placeholder="Enter First Name"
                    fullWidth
                    error={Boolean(errors.firstName)}
                  />
                )}
              />
              {errors.firstName?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-firstName"
                >
                  {errors.firstName?.message}
                </FormHelperText>
              )}
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="lastName">
                Last Name<span style={{ color: 'red' }}> *</span>
              </InputLabel>
              <Controller
                name="lastName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <OutlinedInput
                    id="lastName"
                    type="text"
                    autoComplete={'on'}
                    value={field.value}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    placeholder="Enter Last Name"
                    fullWidth
                    error={Boolean(errors.lastName)}
                  />
                )}
              />
              {errors.lastName?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-lastName"
                >
                  {errors.lastName?.message}
                </FormHelperText>
              )}
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="email-login">
                Email<span style={{ color: 'red' }}> *</span>
              </InputLabel>
              <Controller
                name="email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <OutlinedInput
                    disabled={Boolean(params?.userId)}
                    id="email"
                    type="email"
                    value={field?.value}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    placeholder="Enter Email"
                    autoComplete={'on'}
                    fullWidth
                    error={Boolean(errors.email)}
                  />
                )}
              />
              {errors.email?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-email-login"
                >
                  {errors.email?.message}
                </FormHelperText>
              )}
            </Stack>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="title">Title</InputLabel>
              <Controller
                name="title"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <OutlinedInput
                    id="title"
                    type="text"
                    value={field?.value}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    autoComplete={'on'}
                    placeholder="Enter Title. e.g. CEO, Director etc."
                    fullWidth
                    error={Boolean(errors.title)}
                  />
                )}
              />
              {errors.title?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-title-login"
                >
                  {errors.title?.message}
                </FormHelperText>
              )}
            </Stack>
          </Grid>

          <Grid
            item
            xs={12}
          >
            <Stack spacing={1}>
              <InputLabel htmlFor="timezone">Timezone</InputLabel>
              <TimezoneSelector
                name={'timezone'}
                control={control}
              />
              {/* {errors.timezone?.message && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-timezone"
                >
                  {errors.timezone?.message}
                </FormHelperText>
              )} */}
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Box sx={{ my: '15px' }}>
              <CustomTypography
                sx={{ fontWeight: 500, opacity: 0.5 }}
                variant="body2"
                color={theme.palette.slate[900]}
                width={'155'}
              >
                Group Companies
              </CustomTypography>
            </Box>
            <Box sx={{ mb: '10px' }}>
              {formDetails?.group_companies?.map((company, index) => (
                <Box
                  key={company.id}
                  sx={{ display: 'flex', alignItems: 'center', mb: '10px', opacity: params?.userId ? 0.6 : 1 }}
                >
                  <Box sx={{ width: '40%', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Box sx={{ width: '50px', height: '50px', borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img
                        src={company?.company_thumbnail || company?.company_logo}
                        alt={company?.company_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                    <Typography>{company.company_name}</Typography>
                  </Box>

                  <Box sx={{ width: '60%' }}>
                    <Controller
                      name={`child_tenants.${index}.role`}
                      control={control}
                      render={({ field }) => (
                        <MuiSelect
                          disabled={Boolean(params?.userId)}
                          displayEmpty
                          id="role"
                          value={field?.value ?? ''}
                          name={field.name}
                          onBlur={field.onBlur}
                          autoComplete={'on'}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 250 // Set the desired max height for the dropdown
                              }
                            }
                          }}
                          onChange={(e) => {
                            setValue(`child_tenants.${index}`, {
                              tenant_id: company.id,
                              role: e.target.value
                            });
                            clearErrors(`child_tenants.${index}`);
                            // field.onChange(e);
                            // setUserRole(e.target?.value);
                            // if (e.target.value == roleId?.id) {
                            //   setValue('radioGroup', ['False', 'False']);
                            //   setValue('manager', '');
                            // }
                          }}
                          // onChange={field.onChange}
                          fullWidth
                          error={Boolean(errors.role)}
                        >
                          <MenuItem
                            disabled
                            value=""
                          >
                            Select Role
                          </MenuItem>
                          {USER_ROLES.map((role) => (
                            <MenuItem
                              key={role.tenant_specific_role}
                              value={role.tenant_specific_role}
                            >
                              {role.tenant_specific_role}
                            </MenuItem>
                          ))}
                        </MuiSelect>
                        // <TextField
                        //   {...field}
                        //   select
                        //   fullWidth
                        //   error={!!errors.child_tenants?.[index]?.role}
                        //   helperText={errors.child_tenants?.[index]?.role?.message}
                        // >
                        //   {USER_ROLES.map((role) => (
                        //     <MenuItem
                        //       key={role.tenant_specific_role}
                        //       value={role.tenant_specific_role}
                        //     >
                        //       {role.tenant_specific_role}
                        //     </MenuItem>
                        //   ))}
                        // </TextField>
                      )}
                    />
                    {errors.child_tenants?.[index]?.role?.message && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-role"
                      >
                        {errors.child_tenants?.[index]?.role?.message}
                      </FormHelperText>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Stack
              alignItems={'center'}
              direction={'row'}
              justifyContent="space-between"
              spacing={1}
              sx={{ marginTop: '15px' }}
            >
              <Button
                disableElevation
                onClick={() => navigate(-1)}
                size="large"
                variant="outlined"
                color="secondary"
                sx={{ width: '200px' }}
              >
                Cancel
              </Button>
              <LoadingButton
                disableElevation
                loading={isSubmitting}
                loadingPosition="start"
                disabled={isSubmitting}
                size="large"
                type="submit"
                variant="contained"
                color="primary"
                sx={{ width: '200px' }}
              >
                {params?.userId ? 'Save Changes' : 'Add Group User'}
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
        <ConfirmationDialog
          isOpenModal={isOpenModal}
          setOpenModal={setOpenModal}
          handleClose={handleClose}
          data={{
            title: 'Change CEO?',
            description: `The company already has a CEO. Do you want to change it?`,
            buttonTitle: 'Change'
          }}
        />
        <ConfirmationDialog
          isOpenModal={isOpenHeadModal}
          setOpenModal={setIsOpenHeadModal}
          handleClose={handleCloseHeadModal}
          data={{
            title: 'Change Head of Department?',
            description: `The department already has a Head of Department. Do you want to change it?`,
            buttonTitle: 'Change'
          }}
        />
        <ConfirmationDialog
          isOpenModal={isOpenHODModal}
          setOpenModal={setIsOpenHODModal}
          handleClose={handleCloseHODModal}
          data={{
            title: 'Remove Head of department',
            description: `This will remove the user as Head of this Department.`,
            buttonTitle: 'Change'
          }}
        />
      </Box>
    </form>
  );
};

export default Form;
